import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styles from "./HistoryItem.module.css";
import { DefaultButton } from "@fluentui/react";
import { Delete24Regular } from "@fluentui/react-icons";

export interface HistoryData {
    id: string;
    title: string;
    timestamp: number;
    message_count?: number;
}

interface HistoryItemProps {
    item: HistoryData;
    onSelect: (id: string) => void;
    onDelete: (id: string) => void;
    isActive?: boolean;
}

// Conversations beyond MAX_LENGTH_BAR message-pairs render as a fully-filled bar.
// Log scale so a 30-pair thread isn't 10× longer-bar than a 3-pair one.
const MAX_LENGTH_BAR = 30;
function lengthBarFill(count: number): number {
    if (!count || count <= 0) return 0;
    const ratio = Math.log(count + 1) / Math.log(MAX_LENGTH_BAR + 1);
    return Math.min(1, Math.max(0.05, ratio));
}

export function HistoryItem({ item, onSelect, onDelete, isActive }: HistoryItemProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleDelete = useCallback(() => {
        setIsModalOpen(false);
        onDelete(item.id);
    }, [item.id, onDelete]);

    const count = item.message_count ?? 0;
    const fill = lengthBarFill(count);
    const tooltip = count === 0
        ? "No saved messages yet"
        : `${count} message${count === 1 ? "" : "s"} in this conversation`;

    return (
        <div className={`${styles.historyItem} ${isActive ? styles.historyItemActive : ""}`}>
            <div className={styles.historyItemRow}>
                <button onClick={() => onSelect(item.id)} className={styles.historyItemButton}>
                    <div className={styles.historyItemTitle}>{item.title}</div>
                </button>
                <button onClick={() => setIsModalOpen(true)} className={styles.deleteButton} aria-label="delete this chat history">
                    <Delete24Regular className={styles.deleteIcon} />
                </button>
            </div>
            {count > 0 && (
                <div
                    className={styles.lengthBar}
                    aria-label={tooltip}
                    title={tooltip}
                    role="meter"
                    aria-valuenow={count}
                    aria-valuemin={0}
                    aria-valuemax={MAX_LENGTH_BAR}
                >
                    <div className={styles.lengthBarFill} style={{ width: `${fill * 100}%` }} />
                </div>
            )}
            <DeleteHistoryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onConfirm={handleDelete} />
        </div>
    );
}

function DeleteHistoryModal({ isOpen, onClose, onConfirm }: { isOpen: boolean; onClose: () => void; onConfirm: () => void }) {
    if (!isOpen) return null;
    const { t } = useTranslation();
    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <h2 className={styles.modalTitle}>{t("history.deleteModalTitle")}</h2>
                <p className={styles.modalDescription}>{t("history.deleteModalDescription")}</p>
                <div className={styles.modalActions}>
                    <DefaultButton onClick={onClose} className={styles.modalCancelButton}>
                        {t("history.cancelLabel")}
                    </DefaultButton>
                    <DefaultButton onClick={onConfirm} className={styles.modalConfirmButton}>
                        {t("history.deleteLabel")}
                    </DefaultButton>
                </div>
            </div>
        </div>
    );
}
